import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Container, 
  Box, 
  Typography, 
  FormControlLabel, 
  Checkbox,
  Paper,
  Grid
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, CloudUpload as CloudUploadIcon, Image as ImageIcon } from '@mui/icons-material';
import { 
  getCleaningEquipmentById, 
  createCleaningEquipment, 
  updateCleaningEquipment 
} from '../../services/serviceService';
import { CleaningEquipment } from '../../types/service';

interface CleaningEquipmentFormData {
  name: string;
  description: string;
  imageFile: File | null;
  image_url: string;
  quantity_available: number;
  status: number; // 1 for active, 0 for inactive
  display_order: number;
}

const CleaningEquipmentForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<CleaningEquipmentFormData>({
    name: '',
    description: '',
    imageFile: null,
    image_url: '',
    quantity_available: 0,
    status: 1, // 1 for active, 0 for inactive
    display_order: 0
  });

  useEffect(() => {
    const fetchEquipment = async () => {
      if (!id) return;
      try {
        const equipment = await getCleaningEquipmentById(parseInt(id));
        // Ensure boolean values are properly set
        setFormData({
          name: equipment.name,
          description: equipment.description || '',
          image_url: equipment.image_url || '',
          quantity_available: equipment.quantity_available || 0,
          status: equipment.status === 1 ? 1 : 0, // Convert API number to form number
          display_order: equipment.display_order || 0,
          imageFile: null
        });
      } catch (error) {
        console.error('Error fetching equipment:', error);
        alert('Failed to load equipment data');
      }
    };

    fetchEquipment();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const target = e.target as HTMLInputElement;
    const name = target.name;
    let value: string | boolean | number | File | null = target.value;
    
    if (target.type === 'checkbox') {
      value = target.checked;
    } else if (target.type === 'number') {
      value = Number(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        image_url: URL.createObjectURL(file) // Create a preview URL
      }));
    }
  };

  // Clean up object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (formData.image_url && formData.image_url.startsWith('blob:')) {
        URL.revokeObjectURL(formData.image_url);
      }
    };
  }, [formData.image_url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('quantity_available', formData.quantity_available.toString());
      formDataToSend.append('status', formData.status.toString()); // Convert number to string
      formDataToSend.append('display_order', formData.display_order.toString());

      if (formData.imageFile) {
        formDataToSend.append('image', formData.imageFile);
      } else if (formData.image_url) {
        formDataToSend.append('image_url', formData.image_url);
      }

      if (isEditMode && id) {
        await updateCleaningEquipment(parseInt(id), formDataToSend);
      } else {
        await createCleaningEquipment(formDataToSend);
      }
      
      navigate('/cleaning-equipment');
    } catch (error) {
      console.error('Error saving equipment:', error);
      alert('Failed to save equipment');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ m:0, p:0 }}>
      <Box >
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit' : 'Add New'} Cleaning Equipment
        </Typography>
        
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Display Order"
                  name="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity Available"
                  name="quantity_available"
                  type="number"
                  value={formData.quantity_available}
                  onChange={handleChange}
                  margin="normal"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="status"
                      checked={formData.status === 1}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          status: e.target.checked ? 1 : 0
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Equipment Image
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box sx={{ 
                      width: 150, 
                      height: 150, 
                      border: '1px dashed #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {formData.image_url ? (
                        <img 
                          src={formData.image_url} 
                          alt="Preview" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }} 
                        />
                      ) : (
                        <ImageIcon color="action" sx={{ fontSize: 40, opacity: 0.5 }} />
                      )}
                    </Box>
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                      >
                        {formData.image_url ? 'Change Image' : 'Upload Image'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      {formData.image_url && (
                        <Button
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({
                              ...prev,
                              image_url: '',
                              imageFile: null
                            }));
                          }}
                          sx={{ ml: 1 }}
                        >
                          Remove
                        </Button>
                      )}
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                        Recommended size: 500x500px. JPG, PNG or GIF.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/cleaning-equipment')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CleaningEquipmentForm;
